<template>
  <SectionShell id="sphere" num="05" kicker="The Sphere Lab">
    <template #title>A head is a ball<br />with <span class="sig">features</span><br />stuck on it.</template>
    <template #lede>
      <p>
        Every portrait teacher starts with a sphere and every student wonders why.
        Here's why: a head is round, so the light behaves on a head exactly the way it
        behaves on a ball. Learn the five elements on the ball and you have already
        learned the forehead, the cheek, the chin, the eyeball, the earring and the
        tip of the nose.
      </p>
    </template>

    <div class="lab">
      <figure
        class="stage card"
        ref="stage"
        @pointerdown="onDown"
        @pointermove="onMove"
        @pointerup="dragging = false"
        @pointerleave="dragging = false"
      >
        <svg viewBox="0 0 400 400" class="stage__svg" role="img"
             aria-label="A shaded sphere showing cast shadow, form shadow, halftone, reflected light and full light">
          <defs>
            <radialGradient id="sph" :cx="gx" :cy="gy" r="0.82">
              <stop offset="0%" stop-color="#ffffff" />
              <stop offset="14%" stop-color="#eef1f4" />
              <stop offset="34%" stop-color="#b4bac2" />
              <stop offset="56%" stop-color="#767d87" />
              <stop offset="76%" stop-color="#3a4048" />
              <stop offset="100%" stop-color="#12161b" />
            </radialGradient>
            <radialGradient id="refl" :cx="reflX" :cy="reflY" r="0.42">
              <stop offset="0%" stop-color="#bcc4ce" stop-opacity="0.95" />
              <stop offset="45%" stop-color="#bcc4ce" stop-opacity="0.24" />
              <stop offset="100%" stop-color="#aeb6c0" stop-opacity="0" />
            </radialGradient>
            <radialGradient id="cast">
              <stop offset="0%" stop-color="#05070a" stop-opacity="0.95" />
              <stop offset="55%" stop-color="#05070a" stop-opacity="0.7" />
              <stop offset="100%" stop-color="#05070a" stop-opacity="0" />
            </radialGradient>
            <linearGradient id="ground" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#1b222b" />
              <stop offset="100%" stop-color="#0a0e13" />
            </linearGradient>
            <clipPath id="ballclip"><circle cx="200" cy="186" r="110" /></clipPath>
          </defs>

          <!-- table -->
          <rect x="0" y="292" width="400" height="108" fill="url(#ground)" />
          <line x1="0" y1="292" x2="400" y2="292" stroke="#2c343e" stroke-width="1" />

          <!-- cast shadow -->
          <ellipse
            :cx="castX"
            cy="300"
            :rx="castRX"
            ry="22"
            fill="url(#cast)"
          />

          <!-- the ball -->
          <circle cx="200" cy="186" r="110" fill="url(#sph)" />
          <circle cx="200" cy="186" r="110" fill="url(#refl)" style="mix-blend-mode: screen" />
          <g v-if="poster" clip-path="url(#ballclip)" style="filter: url(#poster5c)">
            <circle cx="200" cy="186" r="110" fill="url(#sph)" />
          </g>

          <!-- the light source itself -->
          <g class="lamp" :transform="`translate(${lampX} ${lampY})`">
            <circle r="17" fill="rgba(255,180,92,0.12)" />
            <circle r="7" fill="#ffb45c" />
            <circle r="7" fill="none" stroke="#ffe6c4" stroke-width="1" />
          </g>
          <line
            :x1="lampX" :y1="lampY" :x2="200 + Math.cos(rad) * 108" :y2="186 - Math.sin(rad) * 108"
            stroke="#ffb45c" stroke-opacity="0.35" stroke-width="1" stroke-dasharray="3 5"
          />

          <!-- labels -->
          <g v-if="labels" font-family="'JetBrains Mono', monospace" font-size="9" letter-spacing="1.2">
            <g v-for="(p, i) in pins" :key="p.k" :opacity="hoverEl === null || hoverEl === i ? 1 : 0.32">
              <line :x1="p.x" :y1="p.y" :x2="p.tx" :y2="p.ty" stroke="#6f7883" stroke-width="0.8" />
              <circle :cx="p.x" :cy="p.y" :r="hoverEl === i ? 4.4 : 2.8" fill="#ffb45c" />
              <text
                :x="p.tx + (p.anchor === 'end' ? -6 : p.anchor === 'start' ? 6 : 0)"
                :y="p.ty + (p.anchor === 'middle' ? 12 : 3.4)"
                :text-anchor="p.anchor"
                :fill="hoverEl === i ? '#ffd9a8' : '#c6ccd4'"
              >{{ p.k }}</text>
            </g>
          </g>
        </svg>

        <figcaption class="stage__cap">
          <span class="mono">drag anywhere to move the light · {{ Math.round(angle) }}°</span>
          <span class="stage__btns">
            <button :class="{ 'is-on': labels }" :aria-pressed="String(labels)" @click.stop="labels = !labels">Labels</button>
            <button :class="{ 'is-on': poster }" :aria-pressed="String(poster)" @click.stop="poster = !poster">Posterise</button>
          </span>
        </figcaption>
      </figure>

      <div class="side">
        <ol class="els">
          <li
            v-for="(e, i) in elements"
            :key="e.n"
            v-reveal="i * 70"
            :class="{ hot: hoverEl === i }"
            @mouseenter="hoverEl = i"
            @mouseleave="hoverEl = null"
          >
            <span class="els__n mono" :style="{ background: e.c, color: i > 2 ? '#05070a' : '#e8ecf1' }">{{ e.n }}</span>
            <div>
              <h3>{{ e.t }}</h3>
              <p>{{ e.p }}</p>
            </div>
          </li>
        </ol>

        <div class="transfer card" v-reveal>
          <h4 class="mono">Now transfer it</h4>
          <p>
            Cover the eyes, nose and mouth on any good portrait with your thumb and what's
            left is this ball. The forehead is full light, the far cheek is form shadow,
            the jaw underside is cast shadow onto the neck, and there is a thin rim of
            reflected light running along the shadowed jawline that most beginners
            forget entirely.
          </p>
          <p class="transfer__note">
            Move the lamp above and watch the whole read of the form change. This is why
            you choose the photograph before you sharpen anything: <b>flat light gives you
            nothing to draw.</b>
          </p>
        </div>
      </div>
    </div>

    <svg class="defs" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <filter id="poster5c" color-interpolation-filters="sRGB">
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncR type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
          <feFuncG type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
          <feFuncB type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
        </feComponentTransfer>
      </filter>
    </svg>
  </SectionShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import SectionShell from '../ui/SectionShell.vue'

const stage = ref(null)
const angle = ref(58) // degrees, 0 = right, 90 = straight up
const labels = ref(true)
const poster = ref(false)
const dragging = ref(false)
const hoverEl = ref(null)

const rad = computed(() => (angle.value * Math.PI) / 180)

/* gradient focal point follows the light */
const gx = computed(() => (0.5 + Math.cos(rad.value) * 0.33).toFixed(4))
const gy = computed(() => (0.5 - Math.sin(rad.value) * 0.33).toFixed(4))

/* reflected light: a soft crescent hugging the rim opposite the light */
const reflX = computed(() => (0.5 - Math.cos(rad.value) * 0.56).toFixed(4))
const reflY = computed(() => (0.5 + Math.sin(rad.value) * 0.56).toFixed(4))

const lampX = computed(() => 200 + Math.cos(rad.value) * 168)
const lampY = computed(() => 186 - Math.sin(rad.value) * 168)

const castX = computed(() => 200 - Math.cos(rad.value) * 118)
const castRX = computed(() => 74 + Math.abs(Math.cos(rad.value)) * 62)

/* Every label is pushed straight out along its own radius, so leader lines
   never cross each other however the lamp is dragged. */
const pins = computed(() => {
  const a = rad.value
  const cx = 200
  const cy = 186
  const at = (deg, r) => {
    const t = a + (deg * Math.PI) / 180
    return [cx + Math.cos(t) * r, cy - Math.sin(t) * r, t]
  }
  const mk = (k, deg, r) => {
    const [x, y] = at(deg, r)
    let [tx, ty, t] = at(deg, 128)
    const c = Math.cos(t)
    const anchor = c > 0.25 ? 'start' : c < -0.25 ? 'end' : 'middle'
    const w = k.length * 6.7 + 14
    // keep the text inside the frame whatever angle the lamp is dragged to
    if (anchor === 'end') tx = Math.max(tx, w + 6)
    else if (anchor === 'start') tx = Math.min(tx, 394 - w)
    else tx = Math.min(Math.max(tx, w / 2 + 6), 394 - w / 2)
    ty = Math.min(Math.max(ty, 22), 372)
    return { k, x, y, tx, ty, anchor }
  }
  return [
    mk('#1 cast shadow', 0, 0), // replaced below
    mk('#2 form shadow', 138, 92),
    mk('#3 halftone', 74, 80),
    mk('#4 reflected light', 176, 103),
    mk('#5 full light', 0, 40)
  ].map((p, i) =>
    i === 0
      ? { k: '#1 cast shadow', x: castX.value, y: 300, tx: castX.value, ty: 336, anchor: 'middle' }
      : p
  )
})

function setFrom(e) {
  const el = stage.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const px = ((e.clientX - r.left) / r.width) * 400
  const py = ((e.clientY - r.top) / r.width) * 400
  const dx = px - 200
  const dy = 186 - py
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI
  if (deg < 0) deg += 360
  angle.value = deg
}
function onDown(e) {
  dragging.value = true
  setFrom(e)
}
function onMove(e) {
  if (dragging.value) setFrom(e)
}

const elements = [
  { n: '#1', c: '#0a0c10', t: 'Cast shadow', p: 'Thrown onto whatever the object sits on. Crisp where it meets the ball, softer and lighter as it runs away. Always the darkest note in the picture.' },
  { n: '#2', c: '#3a4048', t: 'Form shadow', p: 'The band on the ball itself, opposite the light, curving around the form. Do not run it to the outer edge — leave a lane for reflected light.' },
  { n: '#3', c: '#767d87', t: 'Halftone', p: 'The wide middle grey where the surface is turning away from the light. Most of the surface area of most drawings.' },
  { n: '#4', c: '#b6bcc4', t: 'Reflected light', p: 'Bounced light climbing back onto the shadow edge. Faint, easy to miss, and the difference between a sphere and a circle.' },
  { n: '#5', c: '#f4f6f8', t: 'Full light', p: 'The point square-on to the source. Untouched paper. Everything else is measured against it.' }
]
</script>

<style scoped>
.lab {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}

.stage {
  margin: 0;
  overflow: hidden;
  cursor: crosshair;
  touch-action: none;
  position: sticky;
  top: 92px;
  background: #070a0e;
}
.stage__svg { display: block; width: 100%; height: auto; }
.lamp { cursor: grab; }
.stage__cap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-top: 1px solid var(--edge-soft);
  padding: 11px 14px;
  color: var(--light);
  font-size: 9px;
}
.stage__btns { display: flex; gap: 6px; }
.stage__btns button { padding: 6px 11px; font-size: 9px; }

.els { list-style: none; margin: 0 0 28px; padding: 0; }
.els li {
  display: flex;
  gap: 16px;
  padding: 18px 0;
  border-top: 1px solid var(--edge-soft);
}
.els li:first-child { border-top: 0; padding-top: 0; }
.els li { transition: background 0.25s; cursor: default; }
.els li.hot { background: rgba(255, 180, 92, 0.05); }
.els li.hot h3 { color: var(--light); }
.els__n {
  flex: none;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  font-size: 11px;
  letter-spacing: 0.06em;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.els h3 { font-size: 17px; font-weight: 500; margin-bottom: 6px; }
.els p { color: var(--text-dim); font-size: 13.5px; line-height: 1.6; margin: 0; }

.transfer { padding: 24px 26px; }
.transfer h4 { color: var(--signal); font-size: 10px; font-weight: 400; margin-bottom: 14px; }
.transfer p { color: var(--text-dim); font-size: 14px; }
.transfer__note { color: var(--text-faint); }
.transfer__note b { color: var(--light); font-weight: 400; }

.defs { position: absolute; width: 0; height: 0; }

@media (max-width: 980px) {
  .lab { grid-template-columns: 1fr; }
  .stage { position: static; }
}
</style>
